import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { stampsAPI } from '../services/api';
import StampForm from '../components/StampForm';
import StampList from '../components/StampList';
import ChangePasswordModal from '../components/ChangePasswordModal';
import ManageAdminsModal from '../components/ManageAdminsModal';
import './Dashboard.css';

import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LockResetIcon from '@mui/icons-material/LockReset';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

function Dashboard() {
  const [stamps, setStamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStamp, setEditingStamp] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showManageAdmins, setShowManageAdmins] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const PAGE_SIZE = 10;
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.role === 'superadmin';
  const perms = currentUser.permissions || {};

  // Helper — superadmin always has everything
  const can = (key) => isSuperAdmin || !!perms[key];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStamps(searchTerm, page); }, [page]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchStamps = async (search = '', currentPage = page) => {
    try {
      setLoading(true);
      const response = await stampsAPI.getAll(search, currentPage, PAGE_SIZE);
      setStamps(response.data.stamps);
      setPagination(response.data.pagination);
    } catch {
      toast.error('Error fetching stamps');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPage(1);
    fetchStamps(value, 1);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleEdit = (stamp) => {
    if (!can('edit_stamp')) return toast.error('You do not have permission to edit stamps');
    setEditingStamp(stamp);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!can('delete_stamp')) return toast.error('You do not have permission to delete stamps');
    if (!window.confirm('Delete this stamp?')) return;
    try {
      await stampsAPI.delete(id);
      toast.success('Stamp deleted');
      // If last item on page, go back one
      const newPage = stamps.length === 1 && page > 1 ? page - 1 : page;
      setPage(newPage);
      fetchStamps(searchTerm, newPage);
    } catch { toast.error('Error deleting stamp'); }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingStamp(null);
    fetchStamps(searchTerm, page);
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      const response = await stampsAPI.bulkImport(file);
      toast.success(`Imported ${response.data.imported} stamps`);
      if (response.data.errors.length > 0)
        toast.warning(`${response.data.errors.length} rows had errors`);
      setShowBulkImport(false);
      setPage(1);
      fetchStamps(searchTerm, 1);
    } catch { toast.error('Error importing stamps'); }
    finally { setLoading(false); }
  };

  const getInitials = (username) => username ? username.slice(0, 2).toUpperCase() : 'AD';

  return (
    <div className="dashboard">
      {/* ── Header ── */}
      <header className="dashboard-header no-print">
        <div className="container">
          <div className="header-content">
            <div className="header-brand">
              <VerifiedUserIcon className="brand-icon" />
              <div>
                <span className="brand-title">Stamp Verification System</span>
                <span className="brand-sub">Admin Portal</span>
              </div>
            </div>

            <div className="header-user" ref={dropdownRef}>
              <button className="user-profile-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
                <div className="user-avatar">{getInitials(currentUser.username)}</div>
                <div className="user-info">
                  <span className="user-name">{currentUser.username || 'Admin'}</span>
                  <span className="user-role">{currentUser.role || 'admin'}</span>
                </div>
                <KeyboardArrowDownIcon className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <div className="dropdown-avatar">{getInitials(currentUser.username)}</div>
                    <div>
                      <div className="dropdown-username">{currentUser.username}</div>
                      <div className="dropdown-role">{currentUser.role}</div>
                    </div>
                  </div>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item"
                    onClick={() => { setShowChangePassword(true); setDropdownOpen(false); }}>
                    <LockResetIcon fontSize="small" /> Change Password
                  </button>
                  {isSuperAdmin && (
                    <button className="dropdown-item"
                      onClick={() => { setShowManageAdmins(true); setDropdownOpen(false); }}>
                      <AdminPanelSettingsIcon fontSize="small" /> Manage Admins
                    </button>
                  )}
                  <div className="dropdown-divider" />
                  <button className="dropdown-item dropdown-item-danger" onClick={handleLogout}>
                    <LogoutIcon fontSize="small" /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Main content ── */}
      <div className="container">
        <div className="dashboard-actions no-print">
          <div className="search-box">
            <SearchIcon className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, CNIC, stamp number..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="action-buttons">
            {can('add_stamp') && (
              <button onClick={() => { setEditingStamp(null); setShowForm(true); }} className="btn btn-primary">
                <AddCircleIcon fontSize="small" /> Add New Stamp
              </button>
            )}
            {can('bulk_import') && (
              <button onClick={() => setShowBulkImport(!showBulkImport)} className="btn btn-secondary">
                <UploadFileIcon fontSize="small" /> Bulk Import
              </button>
            )}
          </div>
        </div>

        {/* Permission notice for read-only admins */}
        {!isSuperAdmin && !can('add_stamp') && !can('edit_stamp') && !can('delete_stamp') && (
          <div className="perm-notice">
            👁 You have read-only access. Contact your superadmin to request additional permissions.
          </div>
        )}

        {showBulkImport && can('bulk_import') && (
          <div className="card no-print">
            <h3>Bulk Import from CSV</h3>
            <p>Upload a CSV file with stamp data.</p>
            <input type="file" accept=".csv" onChange={handleBulkImport} className="file-input" />
          </div>
        )}

        {loading ? (
          <div className="loading">Loading stamps...</div>
        ) : (
          <StampList
            stamps={stamps}
            onEdit={handleEdit}
            onDelete={handleDelete}
            canEdit={can('edit_stamp')}
            canDelete={can('delete_stamp')}
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            pageSize={PAGE_SIZE}
            onPageChange={(p) => setPage(p)}
          />
        )}
      </div>

      {showForm && <StampForm stamp={editingStamp} onClose={handleFormClose} />}
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
      {showManageAdmins && <ManageAdminsModal onClose={() => setShowManageAdmins(false)} />}
    </div>
  );
}

export default Dashboard;
